import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

const RELOAD_FLAG = 'chunk-reload-attempt';

const isChunkLoadError = (error: unknown): boolean => {
	if (!(error instanceof Error)) return false;
	return (
		error.message.includes('Failed to fetch dynamically imported module') ||
		error.message.includes('Importing a module script failed') ||
		error.message.includes('error loading dynamically imported module') ||
		error.name === 'ChunkLoadError'
	);
};

/**
 * Tras un despliegue, los chunks con hash de la versión anterior desaparecen del
 * servidor y cualquier ruta con `lazy()` que el usuario no hubiera visitado falla.
 * Recargar trae el `index.html` nuevo. Solo se intenta una vez por sesión para no
 * entrar en bucle si el fichero falta de verdad.
 */
export const ChunkLoadErrorBoundary = () => {
	const error = useRouteError();
	const isChunkError = isChunkLoadError(error);

	useEffect(() => {
		if (!isChunkError) return;

		if (sessionStorage.getItem(RELOAD_FLAG)) return;
		sessionStorage.setItem(RELOAD_FLAG, '1');
		window.location.reload();
	}, [isChunkError]);

	useEffect(() => {
		if (!isChunkError) sessionStorage.removeItem(RELOAD_FLAG);
	}, [isChunkError]);

	if (isChunkError) {
		return (
			<div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 px-6 text-center">
				<h2 className="text-xl font-semibold dark:text-white">
					Hay una versión nueva de la aplicación
				</h2>
				<p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
					Estamos recargando para actualizarla. Si esta pantalla no desaparece, recarga la
					página manualmente.
				</p>
			</div>
		);
	}

	throw error;
};
